import React, {
  useState,
  useEffect,
  useCallback,
  Fragment,
  useMemo,
} from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Badge,
  Spinner,
  Pagination,
} from "react-bootstrap";
import Select from "react-select";
import { Country, State, City } from "country-state-city";

import Layout from "../../layout/Layout";
import { useAuth } from "../../context/AuthContext";

import {
  createNavlunFreightPrice,
  getNavlunFreightPriceList,
  updateNavlunFreightPrice,
  deleteNavlunFreightPrice,
} from "../../../api/disticaret/navlunApi";

const initialFormState = {
  country: "",
  destination: "",
  companyName: "",

  transitTimeValue: "",
  transitTimeText: "",

  containerType: "",

  priceAmount: "",
  priceCurrency: "USD",
  priceText: "",

  line: "",

  freeTimeValue: "",
  freeTimeText: "",

  offerDateStartDate: "",
  offerDateEndDate: "",
  offerDateText: "",

  incoterms: "",

  detail: "",
};

const emptyFilters = {
  search: "",
  country: "",
  destination: "",
  companyName: "",
  containerType: "",
  line: "",
  incoterms: "",
  minPrice: "",
  maxPrice: "",
  minFreeTime: "",
  maxFreeTime: "",
  offerDateStartDate: "",
  offerDateEndDate: "",
  showAllDateRange: false,
};

const PAGE_LIMIT = 25;

const COUNTRY_ALIAS_BY_NAME = {
  turkiye: "TR",
  türkiye: "TR",
  turkey: "TR",

  ingiltere: "GB",
  "birlesik krallik": "GB",
  "birleşik krallık": "GB",
  "united kingdom": "GB",

  almanya: "DE",
  germany: "DE",

  belcika: "BE",
  belçika: "BE",
  belgium: "BE",

  italya: "IT",
  italy: "IT",

  ispanya: "ES",
  spain: "ES",

  fransa: "FR",
  france: "FR",

  hollanda: "NL",
  netherlands: "NL",

  polonya: "PL",
  poland: "PL",

  romanya: "RO",
  romania: "RO",

  bulgaristan: "BG",
  bulgaria: "BG",

  abd: "US",
  amerika: "US",
  "amerika birleşik devletleri": "US",
  "amerika birlesik devletleri": "US",
  usa: "US",
  "united states": "US",

  kanada: "CA",
  canada: "CA",

  cin: "CN",
  çin: "CN",
  china: "CN",

  hindistan: "IN",
  india: "IN",

  "birlesik arap emirlikleri": "AE",
  "birleşik arap emirlikleri": "AE",
  "united arab emirates": "AE",

  "suudi arabistan": "SA",
  "saudi arabia": "SA",

  misir: "EG",
  mısır: "EG",
  egypt: "EG",

  fas: "MA",
  morocco: "MA",
};

function NavlunFreightPricePage() {
  const { permissions } = useAuth();

  const canPost = permissions?.includes("navlunFreightPrice_create");
  const canUpdate = permissions?.includes("navlunFreightPrice_update");
  const canDelete = permissions?.includes("navlunFreightPrice_delete");

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [form, setForm] = useState(initialFormState);

  const [filters, setFilters] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);

  const [filterOptions, setFilterOptions] = useState({
    countries: [],
    provinces: [],
    provincesByCountry: {},
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const selectPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: "31px",
      fontSize: "13px",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 8px",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      minHeight: "31px",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  const fmtNum = (num) => {
    const value = Number(num || 0);

    return value.toLocaleString("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDateTR = (dateStr) => {
    if (!dateStr) return "";

    const date = new Date(dateStr);

    if (isNaN(date.getTime())) return dateStr;

    return date.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getFormDate = (dateStr) => {
    if (!dateStr) return "";

    const date = new Date(dateStr);

    if (isNaN(date.getTime())) return "";

    return date.toISOString().split("T")[0];
  };

  const normalizeText = (value = "") => {
    return String(value)
      .toLocaleLowerCase("tr-TR")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/ı/g, "i")
      .replace(/ğ/g, "g")
      .replace(/ü/g, "u")
      .replace(/ş/g, "s")
      .replace(/ö/g, "o")
      .replace(/ç/g, "c")
      .trim();
  };

  const selectFilterOption = (option, rawInput) => {
    const searchValue = normalizeText(rawInput);

    if (!searchValue) return true;

    const label = normalizeText(option.label);
    const value = normalizeText(option.value);
    const searchText = normalizeText(option.data?.searchText || "");

    return (
      label.includes(searchValue) ||
      value.includes(searchValue) ||
      searchText.includes(searchValue)
    );
  };

  const getNestedValue = (obj, path) => {
    if (!path) return undefined;

    return path.split(".").reduce((acc, key) => {
      if (acc === undefined || acc === null) return undefined;
      return acc[key];
    }, obj);
  };

  const trRegionNames = useMemo(() => {
    return new Intl.DisplayNames(["tr"], {
      type: "region",
    });
  }, []);

  const countryOptions = useMemo(() => {
    return Country.getAllCountries()
      .map((country) => {
        const trName = trRegionNames.of(country.isoCode) || country.name;

        return {
          value: country.isoCode,
          label: country.name,
          isoCode: country.isoCode,
          name: country.name,
          searchText: `${country.name} ${trName} ${country.isoCode}`,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, "en"));
  }, [trRegionNames]);

  const selectedCountryOption = useMemo(() => {
    if (!form.country) return null;

    const normalizedCountry = normalizeText(form.country);
    const aliasCountryCode = COUNTRY_ALIAS_BY_NAME[normalizedCountry];

    const found = countryOptions.find((item) => {
      return (
        item.value === form.country ||
        item.value === aliasCountryCode ||
        normalizeText(item.label) === normalizedCountry ||
        normalizeText(item.name) === normalizedCountry
      );
    });

    return (
      found || {
        value: "",
        label: form.country,
        name: form.country,
        searchText: form.country,
      }
    );
  }, [countryOptions, form.country]);

  const selectedCountryCode =
    selectedCountryOption?.isoCode || selectedCountryOption?.value || "";

  const provinceOptions = useMemo(() => {
    if (!selectedCountryCode) return [];

    const states = State.getStatesOfCountry(selectedCountryCode) || [];
    const cities = City.getCitiesOfCountry(selectedCountryCode) || [];

    const optionMap = new Map();

    states.forEach((state) => {
      if (!state?.name) return;

      optionMap.set(`state-${normalizeText(state.name)}`, {
        value: state.name,
        label: state.name,
        type: "province",
        searchText: `${state.name} ${
          state.isoCode || ""
        } province state eyalet`,
      });
    });

    cities.forEach((city) => {
      if (!city?.name) return;

      const stateName =
        states.find((state) => state.isoCode === city.stateCode)?.name || "";

      const label = stateName ? `${city.name} / ${stateName}` : city.name;

      optionMap.set(
        `city-${normalizeText(city.name)}-${normalizeText(stateName)}`,
        {
          value: city.name,
          label,
          type: "city",
          cityName: city.name,
          stateName,
          searchText: `${city.name} ${stateName} city province state şehir eyalet`,
        }
      );
    });

    return Array.from(optionMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label, "en")
    );
  }, [selectedCountryCode]);

  const selectedProvinceOption = useMemo(() => {
    if (!form.destination) return null;

    const normalizedDestination = normalizeText(form.destination);

    const found = provinceOptions.find((item) => {
      return normalizeText(item.label) === normalizedDestination;
    });

    return (
      found || {
        value: form.destination,
        label: form.destination,
        searchText: form.destination,
      }
    );
  }, [provinceOptions, form.destination]);

  const filterCountryOptions = useMemo(() => {
    return (filterOptions.countries || []).map((country) => ({
      value: country,
      label: country,
      searchText: country,
    }));
  }, [filterOptions.countries]);

  const filterProvinceOptions = useMemo(() => {
    const provincesByCountry = filterOptions.provincesByCountry || {};

    if (filters.country) {
      return (provincesByCountry[filters.country] || []).map((province) => ({
        value: province,
        label: province,
        searchText: `${filters.country} ${province}`,
      }));
    }

    return (filterOptions.provinces || []).map((province) => ({
      value: province,
      label: province,
      searchText: province,
    }));
  }, [
    filterOptions.provinces,
    filterOptions.provincesByCountry,
    filters.country,
  ]);

  const selectedFilterCountryOption = filters.country
    ? {
        value: filters.country,
        label: filters.country,
        searchText: filters.country,
      }
    : null;

  const selectedFilterProvinceOption = filters.destination
    ? {
        value: filters.destination,
        label: filters.destination,
        searchText: filters.destination,
      }
    : null;

  const getValidityStatus = (row) => {
    const startDate = row?.offerDate?.startDate;
    const endDate = row?.offerDate?.endDate;

    if (!startDate || !endDate) {
      return {
        key: "UNKNOWN",
        label: "Teklif Tarihi Yok",
        bg: "secondary",
        text: "light",
        rowClass: "",
      };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    if (today < start) {
      return {
        key: "FUTURE",
        label: "Gelecek Teklif",
        bg: "info",
        text: "dark",
        rowClass: "",
      };
    }

    if (today > end) {
      return {
        key: "EXPIRED",
        label: "Teklif Süresi Doldu",
        bg: "danger",
        text: "light",
        rowClass: "table-danger",
      };
    }

    return {
      key: "ACTIVE",
      label: "Geçerli Teklif",
      bg: "success",
      text: "light",
      rowClass: "table-success",
    };
  };

  const renderValidityBadge = (row) => {
    const meta = getValidityStatus(row);

    return (
      <Badge
        bg={meta.bg}
        text={meta.text}
        style={{
          fontSize: "11px",
          padding: "5px 8px",
        }}
      >
        {meta.label}
      </Badge>
    );
  };

  const buildActiveFilters = (filterState = {}) => {
    const activeFilters = {};

    Object.entries(filterState).forEach(([key, value]) => {
      if (typeof value === "boolean") {
        activeFilters[key] = value;
        return;
      }

      if (
        value !== undefined &&
        value !== null &&
        String(value).trim() !== ""
      ) {
        activeFilters[key] = String(value).trim();
      }
    });

    return activeFilters;
  };

  const fetchData = useCallback(async (pageNumber = 1, filterState = {}) => {
    setIsLoading(true);

    try {
      const activeFilters = buildActiveFilters(filterState);

      const params = {
        page: Number(pageNumber) || 1,
        limit: PAGE_LIMIT,
        ...activeFilters,
      };

      const res = await getNavlunFreightPriceList(params);
      const payload = res?.data ? res.data : res;

      if (payload?.success === false) {
        alert(payload.message || "Liste alınırken hata oluştu.");
        return;
      }

      const responseData = payload?.data || {};

      const items =
        responseData.items ||
        responseData.docs ||
        responseData.rows ||
        responseData.list ||
        [];

      const responseFilterOptions = responseData.filterOptions || {};

      setFilterOptions({
        countries: Array.isArray(responseFilterOptions.countries)
          ? responseFilterOptions.countries
          : [],
        provinces: Array.isArray(responseFilterOptions.provinces)
          ? responseFilterOptions.provinces
          : [],
        provincesByCountry:
          responseFilterOptions.provincesByCountry &&
          typeof responseFilterOptions.provincesByCountry === "object"
            ? responseFilterOptions.provincesByCountry
            : {},
      });

      const pagination = responseData.pagination || {};

      const total = Number(
        pagination.totalCount ??
          responseData.total ??
          responseData.totalRecords ??
          responseData.totalCount ??
          responseData.count ??
          0
      );

      const pages = Number(
        pagination.totalPage ??
          responseData.totalPages ??
          responseData.pageCount ??
          responseData.pages ??
          Math.max(1, Math.ceil(total / PAGE_LIMIT))
      );

      const apiCurrentPage = Number(
        pagination.page ??
          responseData.currentPage ??
          responseData.page ??
          pageNumber
      );

      setRows(Array.isArray(items) ? items : []);
      setTotalRecords(Number.isFinite(total) ? total : 0);
      setTotalPages(Number.isFinite(pages) && pages > 0 ? pages : 1);
      setCurrentPage(
        Number.isFinite(apiCurrentPage) && apiCurrentPage > 0
          ? apiCurrentPage
          : pageNumber
      );
    } catch (error) {
      console.error("Navlun liste hatası:", error);

      if (error.response?.status === 403) {
        alert("Bu listeyi görmeye yetkiniz yok.");
      } else {
        alert(error.response?.data?.message || "Liste alınırken hata oluştu.");
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) return;
    fetchData(1, emptyFilters);
  }, [fetchData]);

  const handlePageChange = (pageNumber) => {
    const nextPage = Number(pageNumber);

    if (!Number.isFinite(nextPage)) return;
    if (nextPage < 1) return;
    if (nextPage > totalPages) return;
    if (nextPage === currentPage) return;

    fetchData(nextPage, appliedFilters);
  };

  const handleSearch = (e) => {
    e.preventDefault();

    const nextFilters = {
      ...filters,
    };

    setAppliedFilters(nextFilters);
    setCurrentPage(1);
    fetchData(1, nextFilters);
  };

  const handleClearFilters = () => {
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setCurrentPage(1);
    fetchData(1, emptyFilters);
  };

  const requestSort = (key) => {
    let direction = "asc";

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({
      key,
      direction,
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return " ↕";
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  const buildSavePayload = () => {
    const transitValue =
      form.transitTimeValue === "" ? 0 : Number(form.transitTimeValue);

    const priceAmount = form.priceAmount === "" ? 0 : Number(form.priceAmount);

    const freeTimeValue =
      form.freeTimeValue === "" ? 0 : Number(form.freeTimeValue);

    const currency = form.priceCurrency || "USD";

    const payload = {
      country: form.country || "",
      destination: form.destination || "",
      companyName: form.companyName || "",

      transitTime: {
        value: transitValue,
        unit: "day",
        text: form.transitTimeText || `${transitValue} gün`,
      },

      containerType: form.containerType || "",

      price: {
        amount: priceAmount,
        currency,
        text: form.priceText || `${priceAmount} ${currency}`,
      },

      line: form.line || "",

      freeTime: {
        value: freeTimeValue,
        unit: "day",
        text: form.freeTimeText || `${freeTimeValue} gün`,
      },

      offerDate: {
        startDate: form.offerDateStartDate || null,
        endDate: form.offerDateEndDate || null,
        text:
          form.offerDateText ||
          `${form.offerDateStartDate || ""} - ${form.offerDateEndDate || ""}`,
      },

      incoterms: form.incoterms || "",

      detail: form.detail || "",
    };

    return payload;
  };

  const validateForm = () => {
    if (!form.country) return "Lütfen ülke bilgisini seçiniz.";
    if (!form.destination) return "Lütfen province / şehir bilgisini seçiniz.";
    if (!form.companyName) return "Lütfen firma adını giriniz.";
    if (!form.transitTimeValue) return "Lütfen transit time bilgisini giriniz.";
    if (!form.containerType)
      return "Lütfen type / konteyner bilgisini giriniz.";
    if (!form.priceAmount) return "Lütfen fiyat bilgisini giriniz.";
    if (!form.priceCurrency) return "Lütfen para birimi seçiniz.";
    if (!form.line) return "Lütfen line bilgisini giriniz.";
    if (!form.freeTimeValue) return "Lütfen free time gün bilgisini giriniz.";
    if (!form.offerDateStartDate)
      return "Lütfen teklif başlangıç tarihini giriniz.";
    if (!form.offerDateEndDate) return "Lütfen teklif bitiş tarihini giriniz.";
    if (!form.incoterms) return "Lütfen incoterms bilgisini giriniz.";

    const start = new Date(form.offerDateStartDate);
    const end = new Date(form.offerDateEndDate);

    if (end < start) {
      return "Teklif bitiş tarihi başlangıç tarihinden küçük olamaz.";
    }

    return "";
  };

  const handleSave = async () => {
    const validationMessage = validateForm();

    if (validationMessage) {
      return alert(validationMessage);
    }

    setIsLoading(true);

    try {
      const payload = buildSavePayload();

      if (editRow) {
        payload.id = editRow._id;
        await updateNavlunFreightPrice(payload);
        alert("Navlun fiyat kaydı başarıyla güncellendi.");
      } else {
        await createNavlunFreightPrice(payload);
        alert("Yeni navlun fiyat kaydı başarıyla eklendi.");
      }

      await fetchData(currentPage, appliedFilters);

      setShowModal(false);
      setEditRow(null);
      setForm(initialFormState);
    } catch (error) {
      console.error("Navlun kayıt hatası:", error);
      alert(
        error.response?.data?.message || "İşlem sırasında bir hata oluştu."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteRow) return;

    setIsLoading(true);

    try {
      await deleteNavlunFreightPrice({
        id: deleteRow._id,
      });

      alert("Navlun fiyat kaydı başarıyla silindi.");
      setDeleteRow(null);
      await fetchData(currentPage, appliedFilters);
    } catch (error) {
      console.error("Navlun silme hatası:", error);
      alert(error.response?.data?.message || "Silme işlemi başarısız.");
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateModal = () => {
    setForm(initialFormState);
    setEditRow(null);
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setEditRow(row);

    setForm({
      country: row.country || "",
      destination: row.destination || "",
      companyName: row.companyName || "",

      transitTimeValue: row.transitTime?.value ?? "",
      transitTimeText: row.transitTime?.text || "",

      containerType: row.containerType || "",

      priceAmount: row.price?.amount ?? "",
      priceCurrency: row.price?.currency || "USD",
      priceText: row.price?.text || "",

      line: row.line || "",

      freeTimeValue: row.freeTime?.value ?? "",
      freeTimeText: row.freeTime?.text || "",

      offerDateStartDate: getFormDate(row.offerDate?.startDate),
      offerDateEndDate: getFormDate(row.offerDate?.endDate),
      offerDateText: row.offerDate?.text || "",

      incoterms: row.incoterms || "",

      detail: row.detail || "",
    });

    setShowModal(true);
  };

  const tableColSpan = 10 + (canUpdate ? 1 : 0) + (canDelete ? 1 : 0);

  const sortedRows = useMemo(() => {
    let items = Array.isArray(rows) ? [...rows] : [];

    if (sortConfig.key !== null) {
      items = items.sort((a, b) => {
        let aValue = getNestedValue(a, sortConfig.key);
        let bValue = getNestedValue(b, sortConfig.key);

        if (
          sortConfig.key === "country" ||
          sortConfig.key === "destination" ||
          sortConfig.key === "companyName" ||
          sortConfig.key === "containerType" ||
          sortConfig.key === "line" ||
          sortConfig.key === "incoterms"
        ) {
          aValue = (aValue || "").toString().toLowerCase();
          bValue = (bValue || "").toString().toLowerCase();
        }

        if (
          sortConfig.key === "price.amount" ||
          sortConfig.key === "transitTime.value" ||
          sortConfig.key === "freeTime.value"
        ) {
          aValue = Number(aValue || 0);
          bValue = Number(bValue || 0);
        }

        if (
          sortConfig.key === "offerDate.startDate" ||
          sortConfig.key === "offerDate.endDate"
        ) {
          aValue = new Date(aValue || "1970-01-01").getTime();
          bValue = new Date(bValue || "1970-01-01").getTime();
        }

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;

        return 0;
      });
    }

    return items;
  }, [rows, sortConfig]);

  const analytics = sortedRows.reduce((acc, row) => {
    const currency = row.price?.currency || "N/A";
    const amount = Number(row.price?.amount || 0);
    const status = getValidityStatus(row).key;

    if (!acc[currency]) {
      acc[currency] = {
        totalAmount: 0,
        count: 0,
        activeCount: 0,
        expiredCount: 0,
        futureCount: 0,
        minPrice: null,
        maxPrice: null,
      };
    }

    acc[currency].totalAmount += amount;
    acc[currency].count += 1;

    if (status === "ACTIVE") acc[currency].activeCount += 1;
    if (status === "EXPIRED") acc[currency].expiredCount += 1;
    if (status === "FUTURE") acc[currency].futureCount += 1;

    if (acc[currency].minPrice === null || amount < acc[currency].minPrice) {
      acc[currency].minPrice = amount;
    }

    if (acc[currency].maxPrice === null || amount > acc[currency].maxPrice) {
      acc[currency].maxPrice = amount;
    }

    return acc;
  }, {});

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-end mb-3">
        <div>
          <h4 className="fw-bold text-primary m-0">Navlun Fiyat Takibi</h4>
          <small className="text-muted">
            Toplam {totalRecords} kayıt bulundu.
          </small>
        </div>

        {canPost && (
          <Button
            variant="success"
            size="sm"
            style={{
              whiteSpace: "nowrap",
              height: "fit-content",
            }}
            onClick={openCreateModal}
          >
            Yeni Ekle
          </Button>
        )}
      </div>

      <div className="bg-white p-3 rounded shadow-sm border mb-4">
        <Form onSubmit={handleSearch}>
          <Row className="gy-3 align-items-end">
            <Col md={2}>
              <Form.Label className="small text-muted fw-bold mb-1">
                Genel Arama
              </Form.Label>
              <Form.Control
                type="text"
                size="sm"
                placeholder="Ara..."
                value={filters.search}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    search: e.target.value,
                  })
                }
              />
            </Col>

            <Col md={2}>
              <Form.Label className="small text-muted fw-bold mb-1">
                Ülke
              </Form.Label>
              <Select
                isClearable
                options={filterCountryOptions}
                value={selectedFilterCountryOption}
                filterOption={selectFilterOption}
                placeholder="Ülke seç..."
                noOptionsMessage={() => "Teklif olan ülke yok"}
                styles={selectStyles}
                menuPortalTarget={selectPortalTarget}
                onChange={(selected) =>
                  setFilters({
                    ...filters,
                    country: selected?.value || "",
                    destination: "",
                  })
                }
              />
            </Col>

            <Col md={2}>
              <Form.Label className="small text-muted fw-bold mb-1">
                Province
              </Form.Label>
              <Select
                isClearable
                options={filterProvinceOptions}
                value={selectedFilterProvinceOption}
                filterOption={selectFilterOption}
                placeholder="Province seç..."
                noOptionsMessage={() => "Teklif olan province yok"}
                styles={selectStyles}
                menuPortalTarget={selectPortalTarget}
                onChange={(selected) =>
                  setForm({
                    ...form,
                    destination: selected?.cityName || selected?.value || "",
                  })
                }
              />
            </Col>

            <Col md={2}>
              <Form.Label className="small text-muted fw-bold mb-1">
                Firma
              </Form.Label>
              <Form.Control
                type="text"
                size="sm"
                placeholder="Firma..."
                value={filters.companyName}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    companyName: e.target.value,
                  })
                }
              />
            </Col>

            <Col md={2}>
              <Form.Label className="small text-muted fw-bold mb-1">
                Type
              </Form.Label>
              <Form.Control
                type="text"
                size="sm"
                placeholder="40HC, 20HC, OT..."
                value={filters.containerType}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    containerType: e.target.value,
                  })
                }
              />
            </Col>

            <Col md={2}>
              <Form.Label className="small text-muted fw-bold mb-1">
                Line
              </Form.Label>
              <Form.Control
                type="text"
                size="sm"
                placeholder="MSC, CMA..."
                value={filters.line}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    line: e.target.value,
                  })
                }
              />
            </Col>

            <Col md={2}>
              <Form.Label className="small text-muted fw-bold mb-1">
                Incoterms
              </Form.Label>
              <Form.Control
                type="text"
                size="sm"
                placeholder="DAP, CFR..."
                value={filters.incoterms}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    incoterms: e.target.value.toUpperCase(),
                  })
                }
              />
            </Col>

            <Col md={2}>
              <Form.Label className="small text-muted fw-bold mb-1">
                Min Fiyat
              </Form.Label>
              <Form.Control
                type="number"
                size="sm"
                placeholder="Min"
                value={filters.minPrice}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    minPrice: e.target.value,
                  })
                }
              />
            </Col>

            <Col md={2}>
              <Form.Label className="small text-muted fw-bold mb-1">
                Max Fiyat
              </Form.Label>
              <Form.Control
                type="number"
                size="sm"
                placeholder="Max"
                value={filters.maxPrice}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    maxPrice: e.target.value,
                  })
                }
              />
            </Col>

            <Col md={2}>
              <Form.Label className="small text-muted fw-bold mb-1">
                Min Free Time
              </Form.Label>
              <Form.Control
                type="number"
                size="sm"
                min="0"
                placeholder="Min gün"
                value={filters.minFreeTime}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    minFreeTime: e.target.value,
                  })
                }
              />
            </Col>

            <Col md={2}>
              <Form.Label className="small text-muted fw-bold mb-1">
                Max Free Time
              </Form.Label>
              <Form.Control
                type="number"
                size="sm"
                min="0"
                placeholder="Max gün"
                value={filters.maxFreeTime}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    maxFreeTime: e.target.value,
                  })
                }
              />
            </Col>

            <Col md={2}>
              <Form.Label className="small text-muted fw-bold mb-1">
                Teklif Başlangıç
              </Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={filters.offerDateStartDate}
                disabled={filters.showAllDateRange}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    offerDateStartDate: e.target.value,
                  })
                }
              />
            </Col>

            <Col md={2}>
              <Form.Label className="small text-muted fw-bold mb-1">
                Teklif Bitiş
              </Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={filters.offerDateEndDate}
                disabled={filters.showAllDateRange}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    offerDateEndDate: e.target.value,
                  })
                }
              />
            </Col>

            <Col md={2}>
              <Form.Check
                type="switch"
                id="showAllDateRange"
                label="Tüm Teklif Tarihleri"
                checked={filters.showAllDateRange}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    showAllDateRange: e.target.checked,
                  })
                }
                className="fw-bold small"
              />
              <Form.Text className="text-muted">
                Kapalıysa sadece bugün geçerli teklifler gelir.
              </Form.Text>
            </Col>

            <Col md={12}>
              <div className="d-flex gap-2">
                <Button size="sm" type="submit" variant="primary">
                  Ara
                </Button>

                <Button
                  size="sm"
                  type="button"
                  variant="outline-secondary"
                  onClick={handleClearFilters}
                >
                  Temizle
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </div>

      <Card className="shadow-sm border-0 mb-4">
        <div className="table-responsive">
          {isLoading && !showModal && !deleteRow ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Yükleniyor...</p>
            </div>
          ) : (
            <Table
              bordered
              hover
              size="sm"
              className="mb-0 align-middle"
              style={{
                fontSize: "13px",
                whiteSpace: "nowrap",
              }}
            >
              <thead className="table-light">
                <tr style={{ height: "40px" }}>
                  <th
                    onClick={() => requestSort("country")}
                    style={{ cursor: "pointer" }}
                  >
                    ÜLKE / PROVINCE {getSortIcon("country")}
                  </th>

                  <th
                    onClick={() => requestSort("companyName")}
                    style={{ cursor: "pointer" }}
                  >
                    FİRMA {getSortIcon("companyName")}
                  </th>

                  <th
                    className="text-center"
                    onClick={() => requestSort("transitTime.value")}
                    style={{ cursor: "pointer" }}
                  >
                    TRANSIT TIME {getSortIcon("transitTime.value")}
                  </th>

                  <th
                    className="text-center"
                    onClick={() => requestSort("containerType")}
                    style={{ cursor: "pointer" }}
                  >
                    TYPE {getSortIcon("containerType")}
                  </th>

                  <th
                    className="text-end"
                    onClick={() => requestSort("price.amount")}
                    style={{ cursor: "pointer" }}
                  >
                    FİYAT {getSortIcon("price.amount")}
                  </th>

                  <th
                    className="text-center"
                    onClick={() => requestSort("line")}
                    style={{ cursor: "pointer" }}
                  >
                    LINE {getSortIcon("line")}
                  </th>

                  <th
                    className="text-center"
                    onClick={() => requestSort("freeTime.value")}
                    style={{ cursor: "pointer" }}
                  >
                    FREE TIME {getSortIcon("freeTime.value")}
                  </th>

                  <th
                    onClick={() => requestSort("offerDate.startDate")}
                    style={{ cursor: "pointer" }}
                  >
                    TEKLİF TARİHİ {getSortIcon("offerDate.startDate")}
                  </th>

                  <th
                    className="text-center"
                    onClick={() => requestSort("incoterms")}
                    style={{ cursor: "pointer" }}
                  >
                    INCOTERMS {getSortIcon("incoterms")}
                  </th>

                  <th>DETAY</th>

                  {canUpdate && <th className="text-center">DÜZENLE</th>}
                  {canDelete && <th className="text-center">SİL</th>}
                </tr>
              </thead>

              <tbody>
                {sortedRows.length > 0 ? (
                  sortedRows.map((r) => {
                    const validityMeta = getValidityStatus(r);

                    return (
                      <Fragment key={r._id}>
                        <tr className={validityMeta.rowClass}>
                          <td className="px-3">
                            <div className="fw-bold text-dark">
                              {r.country || "-"}
                            </div>
                            <div
                              className="text-muted"
                              style={{ fontSize: "11px" }}
                            >
                              {r.destination || "-"}
                            </div>
                          </td>

                          <td className="px-3 fw-bold">
                            {r.companyName || "-"}
                          </td>

                          <td className="text-center px-3">
                            {r.transitTime?.text ||
                              `${r.transitTime?.value || "-"} gün`}
                          </td>

                          <td className="text-center px-3">
                            <Badge bg="light" text="dark">
                              {r.containerType || "-"}
                            </Badge>
                          </td>

                          <td className="text-end px-3">
                            <span
                              className="fw-bold text-primary"
                              style={{ fontSize: "14px" }}
                            >
                              {fmtNum(r.price?.amount)}
                            </span>{" "}
                            <span className="text-muted">
                              {r.price?.currency || ""}
                            </span>
                          </td>

                          <td className="text-center px-3">{r.line || "-"}</td>

                          <td className="text-center px-3">
                            <span className="fw-bold">
                              {r.freeTime?.text ||
                                `${r.freeTime?.value || "-"} gün`}
                            </span>
                          </td>

                          <td className="px-3">
                            <div className="fw-bold">
                              {formatDateTR(r.offerDate?.startDate)} -{" "}
                              {formatDateTR(r.offerDate?.endDate)}
                            </div>
                            <div className="mt-1">{renderValidityBadge(r)}</div>
                          </td>

                          <td className="text-center px-3">
                            <Badge bg="secondary">{r.incoterms || "-"}</Badge>
                          </td>

                          <td
                            className="px-3 text-truncate"
                            style={{ maxWidth: "220px" }}
                            title={r.detail}
                          >
                            {r.detail || ""}
                          </td>

                          {canUpdate && (
                            <td className="text-center px-2">
                              <Button
                                size="sm"
                                variant="link"
                                className="p-0 text-decoration-none"
                                style={{ fontSize: "12px" }}
                                onClick={() => openEditModal(r)}
                              >
                                DÜZENLE
                              </Button>
                            </td>
                          )}

                          {canDelete && (
                            <td className="text-center px-2">
                              <Button
                                size="sm"
                                variant="link"
                                className="p-0 text-danger text-decoration-none"
                                style={{ fontSize: "12px" }}
                                onClick={() => setDeleteRow(r)}
                              >
                                SİL
                              </Button>
                            </td>
                          )}
                        </tr>
                      </Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={tableColSpan}
                      className="text-center py-4 text-muted"
                    >
                      Kayıt bulunamadı veya mevcut filtrelere uygun veri yok.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>

        {!isLoading && sortedRows.length > 0 && (
          <div className="d-flex justify-content-between align-items-center bg-light p-2 border-top">
            <small className="text-muted">
              Toplam <b>{totalRecords}</b> kayıttan{" "}
              <b>
                {(currentPage - 1) * PAGE_LIMIT + 1} -{" "}
                {Math.min(currentPage * PAGE_LIMIT, totalRecords)}
              </b>{" "}
              arası.
            </small>

            <Pagination size="sm" className="m-0">
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              />

              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />

              {[...Array(Number(totalPages) || 1)].map((_, i) => {
                const pageNum = i + 1;

                if (
                  totalPages > 10 &&
                  Math.abs(currentPage - pageNum) > 3 &&
                  pageNum !== 1 &&
                  pageNum !== totalPages
                ) {
                  return null;
                }

                return (
                  <Pagination.Item
                    key={pageNum}
                    active={pageNum === currentPage}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </Pagination.Item>
                );
              })}

              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />

              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        )}
      </Card>

      {!isLoading && sortedRows.length > 0 && (
        <div className="mt-4 mb-5">
          <h5
            className="fw-bold text-secondary mb-3"
            style={{ letterSpacing: "-0.5px" }}
          >
            Gösterilen Kayıtların Navlun Özeti
          </h5>

          <Row className="g-3">
            {Object.keys(analytics).length === 0 ? (
              <Col>
                <p className="text-muted small">
                  Analiz edilecek fiyat bilgisi bulunamadı.
                </p>
              </Col>
            ) : (
              Object.entries(analytics).map(([currency, data]) => {
                const avgPrice =
                  data.count > 0 ? data.totalAmount / data.count : 0;

                return (
                  <Col lg={3} md={4} sm={6} key={currency}>
                    <Card
                      className="border-0 shadow-sm h-100"
                      style={{
                        borderRadius: "10px",
                        borderTop: "4px solid #1976d2",
                      }}
                    >
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted fw-bold small">
                            {currency} TOPLAM
                          </span>

                          <Badge bg="primary" className="fs-6">
                            {fmtNum(data.totalAmount)}
                          </Badge>
                        </div>

                        <hr className="my-2" style={{ opacity: 0.1 }} />

                        <div className="d-flex justify-content-between small mb-1">
                          <span className="text-muted">Kayıt:</span>
                          <span className="fw-bold">{data.count}</span>
                        </div>

                        <div className="d-flex justify-content-between small mb-1">
                          <span className="text-muted">Ortalama:</span>
                          <span className="fw-bold text-primary">
                            {fmtNum(avgPrice)}
                          </span>
                        </div>

                        <div className="d-flex justify-content-between small mb-1">
                          <span className="text-muted">Min:</span>
                          <span className="fw-bold text-success">
                            {fmtNum(data.minPrice)}
                          </span>
                        </div>

                        <div className="d-flex justify-content-between small mb-1">
                          <span className="text-muted">Max:</span>
                          <span className="fw-bold text-danger">
                            {fmtNum(data.maxPrice)}
                          </span>
                        </div>

                        <div className="d-flex justify-content-between small mb-1">
                          <span className="text-muted">Geçerli:</span>
                          <span className="fw-bold text-success">
                            {data.activeCount}
                          </span>
                        </div>

                        <div className="d-flex justify-content-between small">
                          <span className="text-muted">Süresi Dolan:</span>
                          <span className="fw-bold text-danger">
                            {data.expiredCount}
                          </span>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })
            )}
          </Row>
        </div>
      )}

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editRow ? "Navlun Kaydını Düzenle" : "Yeni Navlun Kaydı Ekle"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Ülke</Form.Label>
                  <Select
                    isClearable
                    options={countryOptions}
                    value={selectedCountryOption}
                    filterOption={selectFilterOption}
                    placeholder="Ülke ara / seç..."
                    noOptionsMessage={() => "Ülke bulunamadı"}
                    styles={selectStyles}
                    menuPortalTarget={selectPortalTarget}
                    onChange={(selected) =>
                      setForm({
                        ...form,
                        country: selected?.label || "",
                        destination: "",
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Province / Şehir</Form.Label>
                  <Select
                    isClearable
                    isDisabled={!selectedCountryCode}
                    options={provinceOptions}
                    value={selectedProvinceOption}
                    filterOption={selectFilterOption}
                    placeholder={
                      selectedCountryCode
                        ? "Province / şehir ara..."
                        : "Önce ülke seç..."
                    }
                    noOptionsMessage={() => "Province / şehir bulunamadı"}
                    styles={selectStyles}
                    menuPortalTarget={selectPortalTarget}
                    onChange={(selected) =>
                      setForm({
                        ...form,
                        destination: selected?.label || "",
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Firma Adı</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.companyName}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        companyName: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Transit Time</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={form.transitTimeValue}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        transitTimeValue: e.target.value,
                        transitTimeText: e.target.value
                          ? `${e.target.value} gün`
                          : "",
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Type</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="40HC, 20HC, OT..."
                    value={form.containerType}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        containerType: e.target.value.toUpperCase(),
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Fiyat</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.priceAmount}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        priceAmount: e.target.value,
                        priceText: e.target.value
                          ? `${e.target.value} ${form.priceCurrency}`
                          : "",
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group>
                  <Form.Label>Para Birimi</Form.Label>
                  <Form.Select
                    value={form.priceCurrency}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        priceCurrency: e.target.value,
                        priceText: form.priceAmount
                          ? `${form.priceAmount} ${e.target.value}`
                          : "",
                      })
                    }
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="TRY">TRY</option>
                    <option value="GBP">GBP</option>
                    <option value="AED">AED</option>
                    <option value="SAR">SAR</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Line</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="MSC, CMA, ZIM..."
                    value={form.line}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        line: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Incoterms</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="DAP, CFR, DDP..."
                    value={form.incoterms}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        incoterms: e.target.value.toUpperCase(),
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Durum</Form.Label>
                  <div className="pt-2">
                    {renderValidityBadge({
                      offerDate: {
                        startDate: form.offerDateStartDate,
                        endDate: form.offerDateEndDate,
                      },
                    })}
                  </div>
                  <Form.Text className="text-muted">
                    Durum teklif tarih aralığına göre hesaplanır.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Free Time Gün</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={form.freeTimeValue}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        freeTimeValue: e.target.value,
                        freeTimeText: e.target.value
                          ? `${e.target.value} gün`
                          : "",
                      })
                    }
                  />
                  <Form.Text className="text-muted">
                    Konteynerin limanda ücretsiz kalabileceği gün sayısı.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Free Time Text</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.freeTimeText}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        freeTimeText: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Teklif Başlangıç</Form.Label>
                  <Form.Control
                    type="date"
                    value={form.offerDateStartDate}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        offerDateStartDate: e.target.value,
                        offerDateText:
                          e.target.value || form.offerDateEndDate
                            ? `${e.target.value || ""} - ${
                                form.offerDateEndDate || ""
                              }`
                            : "",
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Teklif Bitiş</Form.Label>
                  <Form.Control
                    type="date"
                    value={form.offerDateEndDate}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        offerDateEndDate: e.target.value,
                        offerDateText:
                          form.offerDateStartDate || e.target.value
                            ? `${form.offerDateStartDate || ""} - ${
                                e.target.value || ""
                              }`
                            : "",
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Teklif Tarih Text</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.offerDateText}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        offerDateText: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Detay</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={form.detail}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        detail: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            İptal
          </Button>

          <Button variant="primary" onClick={handleSave} disabled={isLoading}>
            {isLoading ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={deleteRow !== null}
        onHide={() => setDeleteRow(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Kayıt Silme Onayı</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>
            <strong>
              {deleteRow?.country} / {deleteRow?.destination}
            </strong>{" "}
            destinasyonuna ait navlun fiyat kaydını silmek istediğinize emin
            misiniz?
          </p>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteRow(null)}>
            Vazgeç
          </Button>

          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={isLoading}
          >
            {isLoading ? "Siliniyor..." : "Evet, Sil"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
}

export default NavlunFreightPricePage;
